import { TAGS } from '@/shared/query/keys';
import type { EventFrame } from './types';
/**
 * 帧 → 失效 tag。
 *
 * **权威值是后端 `backend/frames/catalog.yaml` 每个帧的 `invalidates` 字段**
 * （ADR 0006 §0：帧的契约面是 `invalidates`，前端据此 refetch 哪个 query tag）。
 *
 * 两处**目录里没有**的条目：
 * - `stream.reconnect` —— 前端自造的控制帧，不落数据。
 * - `redeem.expired` —— 豁免表里的帧（后端判定「不成帧」，`frontend: keep`）
 *   ⇒ 目录里没有它的 `invalidates`，前端沿用既有取值。
 */
export const EVENT_TAG_INVALIDATIONS: Record<EventFrame, readonly string[]> = {
  'stream.reconnect': [],
  'notification.created': [TAGS.notifications],
  'notification.broadcast': [TAGS.notifications],
  'order.account.granted': [TAGS.accounts, TAGS.steamOrders],
  'order.account.revoked': [TAGS.accounts, TAGS.steamOrders],
  'order.account.deleted': [TAGS.accounts, TAGS.steamOrders],
  'cdkey.bound': [TAGS.cdkeys, TAGS.redeems],
  'redeem.card_processed': [TAGS.redeems, TAGS.orders, TAGS.steamOrders, TAGS.accounts],
  'redeem.attribute': [TAGS.redeems],
  'redeem.expired': [TAGS.redeems, TAGS.orders, TAGS.steamOrders],
  'proxy.check_speed_done': [TAGS.proxies, TAGS.operations],
  // 失败帧靠铃铛提示（通知行）；汇率页本身没有可刷的旧数据，不失效 tag。
  'fx.refresh_failed': [],
  // 成功帧：快照已落库 ⇒ 汇率报价页重拉（TAGS.fx，目录 `invalidates: [fx]`）。
  'fx.refreshed': [TAGS.fx],
  'account.online': [TAGS.accounts, TAGS.steamOrders],
  'account.need_code': [TAGS.accounts, TAGS.operations, TAGS.steamOrders],
  'account.failed': [TAGS.accounts, TAGS.steamOrders],
};
export const ALL_DATA_TAGS: readonly string[] = Object.values(TAGS);
export const COALESCE_WINDOW_MS = 250;
export class TagCoalescer {
  private readonly pending = new Set<string>();
  private timer: ReturnType<typeof setTimeout> | null = null;
  constructor(
    private readonly flush: (tags: readonly string[]) => void,
    private readonly windowMs: number = COALESCE_WINDOW_MS,
  ) {}
  push(frame: EventFrame): void {
    for (const tag of EVENT_TAG_INVALIDATIONS[frame]) this.pending.add(tag);
    if (this.timer !== null) return;
    this.timer = setTimeout(() => {
      this.timer = null;
      this.drain();
    }, this.windowMs);
  }
  flushNow(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    this.drain();
  }
  get size(): number {
    return this.pending.size;
  }
  private drain(): void {
    if (this.pending.size === 0) return;
    const tags = [...this.pending];
    this.pending.clear();
    this.flush(tags);
  }
}
